/**
 * "Model Parameters Used" - the modelling team's HEC-RAS record for the
 * selected case, read-only, from /api/hecras/cases.
 *
 * Every row is parsed from a plan, geometry, flow or sediment file and names
 * that file and line. The section never writes to the scenario form on its
 * own: "Copy to inputs" is the only path, and only on a click. A case with no
 * record renders nothing.
 */

import { useState } from 'react'

export interface RegistryRow {
  key: string
  label: string
  value: string | number | boolean
  unit: string
  file: string
  line: number
  xpath?: string
}

export interface RegistryTable {
  title: string
  columns: string[]
  rows: (string | number)[][]
  file: string
  line: number
  xpath?: string
}

export interface CaseRecord {
  case_id: string
  label: string
  site_id: string
  hazard: string
  structure: string
  mechanism: string
  hecras_version: string
  groups: { name: string; rows: RegistryRow[] }[]
  tables: Record<string, RegistryTable>
  form: Record<string, { value: number; source: string }>
}

/** Which table sits under which group. */
const TABLES_BY_GROUP: Record<string, string[]> = {
  Embankment: ['embankment'],
  'Sediment Transport': ['sediment_by_plan', 'grain_classes', 'bed_gradation'],
}

const UNIT_TEXT: Record<string, string> = { m3: 'm³', 'm3/s': 'm³/s', Mm3: 'Mm³', 'kg/m3': 'kg/m³', deg: '°' }

function cell(value: string | number | boolean): string {
  if (typeof value === 'boolean') return value ? 'True' : 'False'
  return String(value)
}

function isNumeric(value: string | number | boolean): boolean {
  return typeof value === 'number'
}

function Table({ table }: { table: RegistryTable }) {
  return (
    <div className="mt-3" data-table={table.title}>
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <span className="section-label">{table.title}</span>
        <span className="truncate font-mono text-12 text-slate-500">
          {table.line > 0 ? `${table.file}:${table.line}` : table.file}
        </span>
      </div>
      <table className="data-table">
        <thead>
          <tr>
            {table.columns.map((c, i) => (
              <th key={c} style={i > 0 && typeof table.rows[0]?.[i] === 'number' ? { textAlign: 'right' } : undefined}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, r) => (
            <tr key={r}>
              {row.map((v, i) => (
                <td key={i} className={typeof v === 'number' ? 'num' : undefined}>
                  {cell(v)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function ModelParametersUsed({
  record,
  onCopy,
}: {
  record: CaseRecord | null
  onCopy: (form: CaseRecord['form']) => void
}) {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState<string | null>(null)
  if (!record) return null

  return (
    <section className="panel" data-testid="model-parameters" data-case={record.case_id} data-open={open}>
      <div className="panel-header">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          className="flex min-h-[32px] flex-1 items-center gap-2 text-left"
          data-testid="model-parameters-toggle"
        >
          <span className="font-mono text-14 text-slate-400" aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          <span className="panel-title">Model Parameters Used</span>
        </button>
        {open && (
          <button
            type="button"
            className="ctl rounded border border-slate-700 px-3 text-14 font-medium text-slate-100 hover:border-slate-500"
            onClick={() => {
              onCopy(record.form)
              setCopied(record.case_id)
            }}
            data-testid="copy-to-inputs"
          >
            {copied === record.case_id ? 'Copied to inputs' : 'Copy to inputs'}
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-5 p-4" data-testid="model-parameters-body">
          <p className="text-14 text-slate-400">
            Values used in the HEC-RAS {record.hecras_version} model for {record.label}.
          </p>
          {record.groups.map((group) => (
            <div key={group.name} data-group={group.name}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{group.name}</th>
                    <th style={{ textAlign: 'right' }}>Value</th>
                    <th>Unit</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row) => (
                    <tr key={row.key} data-param={row.key}>
                      <td className="py-1.5">{row.label}</td>
                      <td className={isNumeric(row.value) ? 'num whitespace-nowrap' : 'py-1.5 text-right font-mono'}>
                        {cell(row.value)}
                      </td>
                      <td className="whitespace-nowrap text-slate-400">{UNIT_TEXT[row.unit] ?? row.unit}</td>
                      <td className="whitespace-nowrap font-mono text-12 text-slate-400" title={row.xpath ?? undefined}>
                        {row.file}:{row.line}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(TABLES_BY_GROUP[group.name] ?? [])
                .map((key) => record.tables[key])
                .filter((t): t is RegistryTable => Boolean(t))
                .map((t) => (
                  <Table key={t.title} table={t} />
                ))}
            </div>
          ))}
        </div>
      )}
    </section>
  )
}
